import { useCurrentStateAndParams } from "@uirouter/react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { ConversionRatesCategoryOverviewListParams } from "@similarweb/pro-api-client";
import { formatDate, fullMonthAndYear } from "@similarweb/pro-logic-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { ICategory, ICustomCategory } from "app/common/services/categoryService.types";
import CountryService from "app/services/CountryService";
import DurationService, { BasicDurations } from "app/services/DurationService";

import { CONVERSION_RATE_TRANSLATION_KEYS, conversionRateFormatters } from "../../consts";
import { ConversionRateSectionHeader } from "../../shared/ConversionRateSectionHeader";
import { resolveCardState } from "../../shared/resolveCardState";

import { ConversionRateMetricCard } from "./ConversionRateMetricCard";
import { useConversionRateOverviewData } from "./useConversionRateOverviewData";

interface IConversionRateOverviewProps {
    primaryCategory: ICategory | ICustomCategory;
    availabilityState: "available" | "locked";
}

export const ConversionRateOverview = ({ primaryCategory, availabilityState }: IConversionRateOverviewProps) => {
    const { translate } = useTranslate();
    const { params } = useCurrentStateAndParams();

    const country = params.country as number;
    const includeSubdomains = (params.includeSubdomains as boolean | undefined) ?? true;
    const durationParam = (params.duration as string) ?? BasicDurations.LAST_MONTH;
    const {
        iso: { from, to },
        isWindow,
    } = DurationService.getDurationData(durationParam, params.comparedDuration as string).forAPI;

    const apiParams = {
        category: primaryCategory.forApi,
        country: country,
        includeSubdomains: includeSubdomains,
        from,
        to,
        isWindow,
    } satisfies ConversionRatesCategoryOverviewListParams;

    const { data, isLoading } = useConversionRateOverviewData(apiParams, {
        enabled: availabilityState === "available",
    });

    if (availabilityState === "available" && !isLoading && !data) {
        return null;
    }

    const countryText = CountryService.getCountryById(country)?.text ?? "";
    const dateRangeText = formatDate(from, to, fullMonthAndYear);
    const cardState = resolveCardState(availabilityState, isLoading);

    return (
        <Flex
            flexDirection="column"
            gap="16px"
            margin="8px 0 0 0"
            data-automation="conversion-rate-section"
        >
            <ConversionRateSectionHeader
                categoryDomainCount={availabilityState === "available" ? data?.categoryDomainCount : undefined}
            />
            <Flex
                gap="24px"
                width="100%"
            >
                <ConversionRateMetricCard
                    automationId="conversion-rate-converted-visits-card"
                    title={translate(CONVERSION_RATE_TRANSLATION_KEYS.totalConvertedVisitsTitle)}
                    tooltip={translate(CONVERSION_RATE_TRANSLATION_KEYS.totalConvertedVisitsTooltip)}
                    dateRangeText={dateRangeText}
                    countryCode={country}
                    countryText={countryText}
                    state={cardState}
                    value={data?.totalConvertedVisits.value}
                    change={data?.totalConvertedVisits.change}
                    formatValue={conversionRateFormatters.convertedVisits}
                    formatChange={conversionRateFormatters.visitsChange}
                />
                <ConversionRateMetricCard
                    automationId="conversion-rate-avg-rate-card"
                    title={translate(CONVERSION_RATE_TRANSLATION_KEYS.avgConversionRateTitle)}
                    tooltip={translate(CONVERSION_RATE_TRANSLATION_KEYS.avgConversionRateTooltip)}
                    dateRangeText={dateRangeText}
                    countryCode={country}
                    countryText={countryText}
                    state={cardState}
                    value={data?.avgConversionRate.value}
                    change={data?.avgConversionRate.change}
                    formatValue={conversionRateFormatters.avgConversionRate}
                    formatChange={conversionRateFormatters.rateChange}
                />
            </Flex>
        </Flex>
    );
};
